import { useState, useEffect } from 'react';

// Global state for dark mode
let globalDarkModeState = false;
let darkModeListeners = [];

const setGlobalDarkMode = (value) => {
  globalDarkModeState = value;
  darkModeListeners.forEach(listener => listener(value));
};

const subscribeToDarkMode = (listener) => {
  darkModeListeners.push(listener);
  return () => {
    darkModeListeners = darkModeListeners.filter(l => l !== listener);
  };
};

export const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Store dark mode state in localStorage to persist across page refreshes
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('stencilDarkMode');
    if (savedDarkMode !== null) {
      const darkModeValue = JSON.parse(savedDarkMode);
      setIsDarkMode(darkModeValue);
      setGlobalDarkMode(darkModeValue);
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    setGlobalDarkMode(newDarkMode);
    localStorage.setItem('stencilDarkMode', JSON.stringify(newDarkMode));
    
    // Apply dark mode to the entire document body
    if (newDarkMode) {
      document.body.classList.add('dark-mode-active');
      document.documentElement.classList.add('dark-mode-active');
    } else {
      document.body.classList.remove('dark-mode-active');
      document.documentElement.classList.remove('dark-mode-active');
    }
  };

  // Apply dark mode on mount
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode-active');
      document.documentElement.classList.add('dark-mode-active');
    } else {
      document.body.classList.remove('dark-mode-active');
      document.documentElement.classList.remove('dark-mode-active');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('dark-mode-active');
      document.documentElement.classList.remove('dark-mode-active');
    };
  }, [isDarkMode]);

  return { isDarkMode, toggleDarkMode };
};

// Hook to get global dark mode state
export const useGlobalDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(globalDarkModeState);

  useEffect(() => {
    const unsubscribe = subscribeToDarkMode(setIsDarkMode);
    return unsubscribe;
  }, []);

  return isDarkMode;
};
